import React from 'react';
import { Layout, Row, Col, Typography, Card, List, Button } from 'antd';
import { 
  BarChartOutlined, 
  FileTextOutlined, 
  UserOutlined, 
  ShoppingCartOutlined,
  DollarOutlined,
  CheckCircleOutlined,

  TeamOutlined,
  InboxOutlined,
  FormOutlined,
  RollbackOutlined,
  LockOutlined,
  SwapOutlined,
  RiseOutlined,
  MessageOutlined,
  CarOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';
import './Features.scss';

const { Content } = Layout;
const { Title, Paragraph } = Typography;

const Features: React.FC = () => {
  const mainFeatures = [
    {
      icon: <BarChartOutlined />,
      title: 'Advanced Sales Management',
      description: 'Comprehensive sales tracking and management system with real-time analytics and performance insights.',
      items: ['Invoice Management', 'Sales Analytics', 'Customer Tracking', 'Performance Reports'],
      color: '#577ae8'
    },
    {
      icon: <DollarOutlined />,
      title: 'Powerful Finance & Accounts',
      description: 'Complete financial management solution with expense tracking, payments, and financial reporting.',
      items: ['Expense Management', 'Payment Processing', 'Financial Reports', 'Budget Planning'],
      color: '#52c41a'
    },
    {
      icon: <FileTextOutlined />,
      title: 'Quotations & Delivery Management',
      description: 'Streamlined quotation and delivery challan system for efficient order processing.',
      items: ['Quotation Creation', 'Delivery Tracking', 'Order Management', 'Status Updates'],
      color: '#fa8c16'
    }
  ];

  const modules = [
    { icon: <TeamOutlined />, title: 'Customer', desc: 'Complete customer profiles and relationship management.' },
    { icon: <UserOutlined />, title: 'Vendor', desc: 'Vendor management and payment tracking system.' },
    { icon: <InboxOutlined />, title: 'Product', desc: 'Product catalog and inventory management.' },
    { icon: <FormOutlined />, title: 'Inventory', desc: 'Real-time inventory tracking and stock management.' },
    { icon: <FileTextOutlined />, title: 'Invoice', desc: 'Automated invoice generation and payment tracking.' },
    { icon: <RollbackOutlined />, title: 'Sales Return', desc: 'Efficient returns and exchange management.' },
    { icon: <LockOutlined />, title: 'Purchase Order', desc: 'Purchase order creation and approval workflow.' },
    { icon: <ShoppingCartOutlined />, title: 'Purchase', desc: 'Complete purchasing process management.' },
    { icon: <SwapOutlined />, title: 'Purchase Return', desc: 'Purchase returns and supplier management.' },
    { icon: <RiseOutlined />, title: 'Expenses', desc: 'Expense tracking and financial management.' },
    { icon: <MessageOutlined />, title: 'Quotations', desc: 'Quotation creation and management system.' },
    { icon: <CarOutlined />, title: 'Delivery Challan', desc: 'Delivery tracking and logistics management.' }
  ];

  return (
    <Layout className="features-layout">
      <Content>
        {/* Hero Section */}
        <section className="features-hero">
          <div className="container">
            <div className="hero-content">
              <div className="features-badge">Advanced Features</div>
              <Title level={1} className="hero-title">
                Powerful Features to <span className="highlight">Transform</span> Your Business
              </Title>
              <Paragraph className="hero-description">
                Discover comprehensive tools designed to streamline your operations, enhance productivity, and drive growth across all aspects of your business.
              </Paragraph>
              <Button type="primary" size="large" className="cta-button">
                Explore Features <ArrowRightOutlined />
              </Button>
            </div>
          </div>
        </section>

        {/* Main Features Section */}
        <section className="main-features-section">
          <div className="container">
            <div className="section-header">
              <Title level={2} className="section-title">
                Core Business Solutions
              </Title>
              <Paragraph className="section-description">
                Comprehensive tools designed to address every aspect of your business operations with precision and efficiency.
              </Paragraph>
            </div>

            <Row gutter={[32, 32]}>
              {mainFeatures.map((feature, index) => (
                <Col xs={24} lg={8} key={index}>
                  <Card className="feature-card" hoverable>
                    <div className="feature-icon" style={{ color: feature.color }}>
                      {feature.icon}
                    </div>
                    <Title level={3} className="feature-title">{feature.title}</Title>
                    <Paragraph className="feature-description">
                      {feature.description}
                    </Paragraph>
                    <List
                      className="feature-list"
                      dataSource={feature.items}
                      renderItem={(item) => (
                        <List.Item>
                          <CheckCircleOutlined className="list-icon" style={{ color: feature.color }} />
                          <span className="list-text">{item}</span>
                        </List.Item>
                      )}
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </section>

        {/* Modules Section */}
        <section className="modules-section">
          <div className="container">
            <div className="section-header">
              <div className="module-badge">Integrated Modules</div>
              <Title level={2} className="section-title">
                Complete <span className="highlight">Module Ecosystem</span>
              </Title>
              <Paragraph className="section-description">
                Seamlessly integrated modules that work together to provide a comprehensive business management solution.
              </Paragraph>
            </div>

            <Row gutter={[24, 24]}>
              {modules.map((module, index) => (
                <Col xs={24} sm={12} md={8} lg={6} key={index}>
                  <Card className="module-card" hoverable>
                    <div className="module-icon-container">
                      {module.icon}
                    </div>
                    <Title level={5} className="module-title">{module.title}</Title>
                    <Paragraph className="module-description">
                      {module.desc}
                    </Paragraph>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </section>

        {/* Invoice Templates Section */}
        <section className="invoice-templates-section">
          <div className="container">
            <div className="section-header">
              <div className="templates-badge">Multiple Different Invoices</div>
              <Title level={2} className="section-title">
                You Can Select your Own <span className="highlight">Invoice Template</span>
              </Title>
              <Paragraph className="section-description">
                Discover the core pillars of our platform - quality Scores, expert software support, and tailored management assistance.
              </Paragraph>
            </div>

            <div className="templates-grid">
              <div className="template-item">
                <div className="template-image">
                  <img src="/images/invoice-templates/invoice_1.png" alt="Standard Invoice" />
                </div>
                <div className="template-label">Standard</div>
              </div>
              
              <div className="template-item">
                <div className="template-image">
                  <img src="/images/invoice-templates/invoice_2.png" alt="Modern Invoice" />
                </div>
                <div className="template-label">Modern</div>
              </div>
              
              <div className="template-item">
                <div className="template-image">
                  <img src="/images/invoice-templates/invoice_3.png" alt="Professional Invoice" />
                </div>
                <div className="template-label">Professional</div>
              </div>
            </div>
          </div>
        </section>
      </Content>
    </Layout>
  );
};

export default Features; 